import { Stack, Text } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { settings } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * `verified` has three states and they mean three different things.
 *
 * - `true`: every dump the manifest names is in the store at the recorded size.
 * - `false`: one is missing or the wrong size, which means the snapshot cannot
 *   be restored whole. The offending databases are named, because "incomplete"
 *   without a name sends an operator to list a bucket by hand.
 * - `null`: the store could not be listed, so nothing was checked. Rendering
 *   that as a pass would be the one failure this page exists to prevent.
 */
export function VerifiedBadge({ snapshot, withDetail = false }: {
  snapshot: settings.BackupSnapshot;
  /** Names the failing databases under the badge. For the table, where there is room. */
  withDetail?: boolean;
}) {
  const t = useT();

  if (snapshot.verified === true) {
    return <StatusBadge status="healthy" size="xs">{t('system.backups.verified.yes')}</StatusBadge>;
  }

  if (snapshot.verified === false) {
    // An encrypted copy is legitimately larger than the dump it came from, so a
    // size mismatch only counts against an unencrypted snapshot.
    const sizeChecked = snapshot.encryption === 'none';
    const missing = snapshot.databases
      .filter((d) => d.present === false || (sizeChecked && d.stored_bytes != null && d.stored_bytes !== d.bytes))
      .map((d) => d.database);
    const badge = <StatusBadge status="down" size="xs">{t('system.backups.verified.no')}</StatusBadge>;
    if (!withDetail || missing.length === 0) return badge;
    return (
      <Stack gap="xs">
        {badge}
        <Text role="caption" c="var(--rr-color-error)">
          {t('system.backups.verified.missing', { databases: missing.join(', ') })}
        </Text>
      </Stack>
    );
  }

  return <StatusBadge status="inactive" size="xs">{t('system.backups.verified.unknown')}</StatusBadge>;
}
